import { ChangeDetectorRef, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Router } from "@angular/router";
import { Alert } from "@models/alerts/alert";
import { EntityType } from '@models/common/entity-type.enum';
import { ExportTypeEnum } from "@models/common/export-type.enum";
import { Company } from "@models/companies/company";
import { CompanyRecord } from '@models/companies/company-record';
import { User } from "@models/organisations/user";
import { CompanyChangeOfficerType } from "@models/relationships/company-change-officer-type";
import { ConfirmationRequiredResponse } from "@models/requests/confirmation-required-response";
import {
  NgbDropdown,
  NgbDropdownItem,
  NgbDropdownMenu,
  NgbDropdownToggle,
  NgbModal,
  NgbTooltip
} from '@ng-bootstrap/ng-bootstrap';
import {
  CompanyFormPartnerAccount
} from '@pages/companies/components/companies-page/components/company-form-partner-account/company-form-partner-account.component';
import { CompaniesGridService } from "@pages/companies/services/companies-grid.service";
import { CompanyExportService } from "@pages/companies/services/company-export.service";
import {
  ManageTagsModalComponent
} from '@pages/settings/components/manage-tags-page/manage-tags-modal/manage-tags-modal.component';
import { AlertsListComponent } from "@shared/components/alerts-list/alerts-list.component";
import {
  CommonActionsModalComponent,
  IActionButtonConfig
} from "@shared/components/commons/common-actions-modal/common-actions-modal.component";
import { CommonPageComponent } from "@shared/components/commons/common-page/common-page.component";
import {
  AgCompanyActionsComponent
} from "@shared/components/grid/ag-cells/companies/ag-company-actions/ag-company-actions.component";
import {
  GridClientSideComponent
} from "@shared/components/grid/components/grid-client-side/grid-client-side.component";
import { NotesComponent } from '@shared/components/notes/notes.component';
import { ButtonComponent } from '@shared/controls/button/button.component';
import { HasRoleDirective } from '@shared/directives/has-role.directive';
import { HideProdDirective } from "@shared/directives/hide-prod.directive";
import { CompaniesApiService } from "@shared/services/companies-api.service";
import { IPartialHeaderData } from "@shared/services/header.service";
import { OrganisationApiService } from "@shared/services/organisation-api.service";
import { PageSizeService } from "@shared/services/page-size.service";
import { CsvExportParams, ExcelExportParams, GridApi, } from "ag-grid-community";
import { ToastrService } from "ngx-toastr";
import { catchError, filter, from, Observable, of, switchMap, take, tap, throwError } from 'rxjs';
import {
  BulkCompanyChangeOfficerAppointComponent,
  BulkCompanyChangeOfficerCeaseComponent,
  BulkCompanyForm361Component
} from "../../../../forms/asic-forms/bulks";
import {
  CompanyChangeOfficer,
  CompanyForm361,
  CompanyForm362,
  CompanyRegistration
} from '../../../../forms/models/company';
import { ModalFormsService } from '../../../../forms/services/modal-forms.service';

@Component({
  selector: 'app-companies',
  templateUrl: 'companies-page.component.html',
  styleUrl: 'companies-page.component.scss',
  standalone: true,
  imports: [
    ButtonComponent,
    NotesComponent,
    NgbDropdown,
    NgbDropdownToggle,
    NgbDropdownMenu,
    NgbDropdownItem,
    AlertsListComponent,
    HasRoleDirective,
    HideProdDirective,
    NgbTooltip,
    GridClientSideComponent,
  ],
  providers: [
    CompaniesGridService,
    CompanyExportService,
  ]
})
export class CompaniesPageComponent extends CommonPageComponent implements OnInit {
  companiesApiService = inject(CompaniesApiService);
  companiesGridService = inject(CompaniesGridService);
  companyExportService = inject(CompanyExportService);
  organisationService = inject(OrganisationApiService);
  modalFormsService = inject(ModalFormsService);
  modalService = inject(NgbModal);
  toastr = inject(ToastrService);
  cdr = inject(ChangeDetectorRef);
  router = inject(Router);
  destroyRef = inject(DestroyRef);

  readonly pageTitle = 'Companies';
  readonly EntityType = EntityType;
  override readonly headerData: IPartialHeaderData = {
    title: this.pageTitle,
    iconClass: 'ic-companies-20',
  };

  alerts: Alert[] = [];
  companiesList: CompanyRecord[] = [];
  checkedCompanies: CompanyRecord[] = [];
  notesVisible = false;
  alertsVisible = false;
  entityForNotes: CompanyRecord | undefined | null = null;
  entityForAlerts: CompanyRecord | undefined | null = null;

  isCompanyListRefreshLoading = signal(false);

  override ngOnInit() {
    super.ngOnInit();
    this.setColDefs();
    this.loadCompanyList();
    this.companiesGridService.refreshFiltersTrigger$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.companiesGridService.setGridFilterOptions(this.companiesList));
  }

  loadCompanyList(): void {
    this.isLoading.set(true);
    this.companiesApiService.list()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError((error) => {
          this.isLoading.set(false);
          const errorMsg = 'Failed to load companies';
          this.toastr.error(errorMsg);
          console.error(error);
          return throwError(() => new Error(errorMsg));
        })
      )
      .subscribe(companies => {
        const users = this.organisationService.getCachedProfileUsers();
        this.companiesList = companies.map((company) => {
          users.forEach(u => {
            if (u.id === company.accountManagerId) {
              company.accountManager = (u as unknown as User);
            }

            if (u.id === company.partnerManagerId) {
              company.partnerManager = (u as unknown as User);
            }
          });

          return company;
        });

        this.companiesGridService.setGridFilterOptions(this.companiesList);
        this.setColDefs();
        this.isLoading.set(false);
      });
  }


  resetSelectedRows(): void {
    this.companiesGridService.gridApi.deselectAll();
  }

  openAppointCompanyForm(): void {
    if (this.modalOpened()) return;
    const modalRef = this.modalFormsService.openModalWithCompany(new CompanyForm362(), new Company());

    modalRef.closed
      .pipe(
        filter((data) => !!data),
        switchMap(() => {
          this.isLoading.set(true);
          return this.companiesApiService.list();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((res) => {
        this.companiesList = res;
        this.isLoading.set(false);
      });
  }

  openCompanyRegistrationForm(isSpecialPurpose = false): void {
    if (this.modalOpened()) return;
    const modalRef = this.modalFormsService.openModalWithCompany(new CompanyRegistration({ isSpecialPurpose }), new Company());

    modalRef.closed
      .pipe(
        filter((data) => !!data),
        switchMap(() => {
          this.isLoading.set(true);
          return this.companiesApiService.list();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((res) => {
        this.companiesList = res;
        this.isLoading.set(false);
      });
  }

  openManageTagsModal(): void {
    const modalRef = this.modalService.open(
      ManageTagsModalComponent,
      {
        windowClass: 'manage-tags-modal',
        size: 'xl',
        centered: true
      });
    const instance = modalRef.componentInstance as ManageTagsModalComponent;

    instance.entities = this.checkedCompanies;
    instance.confirm
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.loadCompanyList();
        this.selectCompanies([]);
      });
  }

  redirectToChangeAddressInBulk(): void {
    const entityIds = this.checkedCompanies.map(company => company.entityId);

    void this.router.navigate(['/bulk-changes'], {
      queryParams: { entityIds }
    });
  }

  openBulkAppointOfficeholderForm(): void {
    if (this.modalOpened()) return;

    const modalRef = this.modalFormsService.openModalWithBulkCompany(new CompanyChangeOfficer(), new Company(), true, {}, {});
    const instance = modalRef.componentInstance as BulkCompanyChangeOfficerAppointComponent;

    instance.companyRecords = this.checkedCompanies;

    modalRef.result.then(() => {
      this.resetSelectedRows();
      this.selectCompanies([]);
    }, () => {});
  }

  openBulkCeaseOfficeholderForm(): void {
    if (this.modalOpened()) return;

    const modalRef = this.modalFormsService.openModalWithBulkCompany(
      new CompanyChangeOfficer({ actionType: CompanyChangeOfficerType.Removal }),
      new Company(),
    );

    const instance = modalRef.componentInstance as BulkCompanyChangeOfficerCeaseComponent;
    instance.companyRecords = this.checkedCompanies;

    modalRef.result.then(() => {
      this.resetSelectedRows();
      this.selectCompanies([]);
    }, () => {});
  }

  openBulkCeaseRegisteredAgentForm(): void {
    if (this.modalOpened()) return;

    const modalRef = this.modalFormsService.openModalWithBulkCompany(new CompanyForm361(), new Company());
    const instance = modalRef.componentInstance as BulkCompanyForm361Component;

    instance.companyRecords = this.checkedCompanies;

    modalRef.result.then(
      () => {
        this.resetSelectedRows();
        this.selectCompanies([]);
      },
      () => {
      });
  }

  openEditPartnerAndAccountManagerModal(company: CompanyRecord | null): void {
    if (this.modalOpened()) return;

    const modalRef = this.modalFormsService.openModal(
      CompanyFormPartnerAccount,
      { size: 'sm' }
    );
    modalRef.result.then(
      () => {
        this.companiesList = [...this.companiesList];
        this.cdr.detectChanges();
        this.resetSelectedRows();
        this.selectCompanies([]);
      },
      () => {
      });

    const instance = modalRef.componentInstance as CompanyFormPartnerAccount;
    instance.companies = company ? [company] : this.checkedCompanies;
  }

  appointAccountManager(company: Company): void {
    console.log(company);
  }

  downloadCompanyProfilePdf(company: CompanyRecord | null): void {
    if (!company || this.modalOpened()) return;
    this.companyExportService.downloadCompanyProfilePdf(company);
  }

  openAlerts(data: CompanyRecord): void {
    if (this.modalOpened()) return;

    this.alertsVisible = true;
    this.alerts = data.alerts;
    this.entityForAlerts = data;
  }

  onRefreshCompanyList(): void {
    this.isCompanyListRefreshLoading.set(true);

    this.refreshCompaniesList(false)
      .subscribe({
        next: (confirmationRequestResponse) => {
          if (!confirmationRequestResponse.confirmationRequired) return;

          const modalRef = this.modalService.open(CommonActionsModalComponent, { size: 'sm' });
          const instance = modalRef.componentInstance as CommonActionsModalComponent;

          const body = `
              <div class="text-center">A similar refresh request was completed today, and the result is available on your Alerts page.</div>
              <div class="text-center">Do you still want to submit a new request?</div>
          `;

          const actions: IActionButtonConfig[] = [
            {
              text: 'Yes',
              btnColor: 'orange',
              action: this.refreshCompaniesList(true).pipe(catchError(() => of(null))),
            },
            {
              text: 'Cancel',
              btnColor: 'white',
              action: of(null),
            },
          ];

          instance.config = {
            ...instance.config,
            header: 'Submit new Request',
            actions,
            body
          };

          from(modalRef.result).pipe(take(1));
        },
        error: (error) => {
          console.warn('[refreshCompaniesList] error: ', error);
        }
      });
  }

  refreshCompaniesList(force = true): Observable<ConfirmationRequiredResponse> {
    const successMessage = 'Your request to refresh the company list has been submitted to ASIC. We’ll notify you with an alert once a response is received.';

    return this.companiesApiService.refreshList(force)
      .pipe(
        tap({
          next: (confirmationRequestResponse) => {
            this.isCompanyListRefreshLoading.set(false);

            if (confirmationRequestResponse.success) {
              this.toastr.success(confirmationRequestResponse.message ?? successMessage);
            } else {
              this.toastr.info(confirmationRequestResponse.message ?? '');
            }
          },
          error: (error) => {
            console.log(error);
            this.isCompanyListRefreshLoading.set(false);
          }
        })
      );
  }

  onAlertReaded(alertId: string): void {
    if (!this.entityForAlerts) return;

    const relatedCompany = this.companiesList.find(companyRecord => companyRecord.entityId == this.entityForAlerts?.entityId);
    const relatedAlertIndex = relatedCompany?.alerts.findIndex(alert => alert.id == alertId);

    if (relatedCompany && relatedAlertIndex != undefined && relatedAlertIndex > -1) {
      this.companiesGridService.gridApi.refreshCells({ force: true });
    }
  }

  openNote(data: CompanyRecord): void {
    if (this.modalOpened()) return;
    this.notesVisible = true;
    this.entityForNotes = data;
  }

  get modalOpened() {
    return this.modalFormsService.modalOpened;
  }

  // =====  Grid  =====

  readonly pinFirstColumnName = 'name';
  readonly defaultPageSize = PageSizeService.defaultPageSize;

  colDefs = this.companiesGridService.colDefs.asReadonly();

  onGridReady(gridApi: GridApi): void {
    this.companiesGridService.setGridApi(gridApi);
  }

  selectCompanies(companies: CompanyRecord[]): void {
    this.checkedCompanies = companies;
  }

  setColDefs(): void {
    this.companiesGridService.setColDefs({
      field: '',
      width: 150,
      sortable: false,
      cellClass: 'actions-button-cell',
      cellRenderer: AgCompanyActionsComponent,
      cellRendererParams: {
        appointAccountManager: this.appointAccountManager.bind(this),
        editPartnerAndAccountManager: this.openEditPartnerAndAccountManagerModal.bind(this),
        downloadCompanyProfilePdf: this.downloadCompanyProfilePdf.bind(this),
        exportCompanyProfile: this.exportCompanyProfile.bind(this),
        openAlerts: this.openAlerts.bind(this),
        onOpenNotes: this.openNote.bind(this)
      },
      suppressHeaderMenuButton: true,
    });
  }

  // =====  Export  =====

  readonly excelExportParams = this.getExportParamsXls() as ExcelExportParams;
  readonly csvExportParams = this.getExportParamsCsv() as unknown as CsvExportParams;
  readonly excelStyles = this.companyExportService.excelStyles;

  exportSelectedCompaniesToXls(): void {
    this.exportCompanyList(true, ExportTypeEnum.EXCEL);
  }

  exportSelectedCompaniesToCsv(): void {
    this.exportCompanyList(true, ExportTypeEnum.CSV);
  }

  exportCompanyList(isBulkExport: boolean, exportType = ExportTypeEnum.EXCEL): void {
    if (this.modalOpened()) return;

    this.companyExportService.exportCompanyList(
      this.companiesGridService.gridApi,
      isBulkExport,
      exportType,
      this.colDefs(),
      this.checkedCompanies
    );
  }

  getExportParamsXls() {
    return this.companyExportService.getExportParamsXls();
  }

  getExportParamsCsv() {
    return this.companyExportService.getExportParamsCsv();
  }

  exportCompanyProfile(company: CompanyRecord): void {
    this.companyExportService.exportCompanyProfile(
      this.companiesGridService.gridApi,
      company,
      this.colDefs()
    );
  }
}
